import { BACKEND_URL } from "../lib/backend";
/**
 * ApiDocsPage — BETA API documentation page.
 * Route: /developers (auth required)
 *
 * Lists all external endpoints protected by X-API-Key with cURL / JS /
 * Python examples, and a manual access request form that emails the admin.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Key, Code2, Copy, Check, Send, Sparkles, Lock, Activity, Heart,
  Newspaper, Headphones, FileText, TrendingUp, Database, Radio, Layers, BookOpenCheck,
} from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";

const API = BACKEND_URL + "/api";
const BASE = BACKEND_URL; // for examples shown to user

const ENDPOINTS = [
  {
    id: "feed",
    method: "GET",
    path: "/api/external/feed",
    icon: Newspaper,
    name: "Articles feed",
    description: "Paginated news articles from the last 90 days. Full-text search, category filter, optional Reddit inclusion.",
    params: [
      { name: "page", type: "int", required: false, default: "1" },
      { name: "limit", type: "int (1–100)", required: false, default: "20" },
      { name: "search", type: "string", required: false, default: "—" },
      { name: "category", type: "string", required: false, default: "—" },
      { name: "include_reddit", type: "bool", required: false, default: "false" },
      { name: "sort", type: "string", required: false, default: "recent", note: "recent | smart (soft-ranking; offset pagination only)" },
    ],
    sample: `{
  "articles": [
    {
      "id": "abc-123",
      "title": "Hotel chains pilot agentic AI booking",
      "source": "Skift",
      "category": "Hotel Tech",
      "summary": "...",
      "originalUrl": "https://...",
      "publishedAt": "2026-02-10T08:00:00+00:00"
    }
  ],
  "totalCount": 412,
  "totalPages": 21,
  "currentPage": 1
}`,
  },
  {
    id: "brief",
    method: "GET",
    path: "/api/external/brief",
    icon: FileText,
    name: "Brief of the Week",
    description: "Latest published Travel Brief (falls back to most recent draft with content). Ideal for Slack/Telegram relays.",
    params: [],
    sample: `{
  "brief": {
    "slug": "ai-everywhere-feb-10",
    "title": "AI Everywhere: Week of Feb 10",
    "content": "## Top stories...",
    "is_published": true,
    "published_at": "2026-02-10T07:00:00+00:00"
  }
}`,
  },
  {
    id: "podcast",
    method: "GET",
    path: "/api/external/podcast",
    icon: Headphones,
    name: "Podcast episodes",
    description: "Published podcast episodes (newest first) plus a `latest` shortcut. Useful for Friday-morning automation.",
    params: [
      { name: "limit", type: "int (1–50)", required: false, default: "10" },
    ],
    sample: `{
  "latest": {
    "id": "ep-42",
    "title": "TourismIntel Weekly · Feb 14",
    "spreaker_episode_url": "https://...",
    "published_at": "2026-02-14T07:00:00+00:00"
  },
  "episodes": [...],
  "total": 10
}`,
  },
  {
    id: "newsletter",
    method: "GET",
    path: "/api/external/newsletter",
    icon: Database,
    name: "Newsletters",
    description: "Published newsletters with rendered HTML body and metadata. Perfect for archive mirrors.",
    params: [
      { name: "limit", type: "int (1–50)", required: false, default: "10" },
    ],
    sample: `{
  "latest": {
    "id": "nl-218",
    "title": "Friday Digest · Feb 14",
    "html": "<p>...</p>",
    "published_at": "2026-02-14T07:00:00+00:00"
  },
  "newsletters": [...],
  "total": 10
}`,
  },
  {
    id: "top-performers",
    method: "GET",
    path: "/api/external/top-performers",
    icon: Activity,
    name: "Top performers",
    description: "Articles ranked by signal_score (ticker picks +5 · brief inclusions +8 · public clicks +1) with topic aggregates.",
    params: [
      { name: "limit", type: "int (1–50)", required: false, default: "10" },
      { name: "days", type: "int (1–30)", required: false, default: "7" },
      { name: "exclude_reddit", type: "bool", required: false, default: "true" },
    ],
    sample: `{
  "items": [
    {
      "title": "...",
      "signal_score": 14,
      "public_clicks": 7,
      "is_trending": true
    }
  ],
  "top_topics": [
    { "category": "AI", "total_signal": 78, "article_count": 12 }
  ],
  "window_days": 7
}`,
  },
  {
    id: "trending",
    method: "GET",
    path: "/api/external/trending",
    icon: TrendingUp,
    name: "Trending now",
    description: "Articles flagged as trending via early-signal detection (4+ unique clicks within 6h). Recency window adjustable.",
    badge: "NEW",
    params: [
      { name: "limit", type: "int (1–50)", required: false, default: "20" },
      { name: "hours", type: "int (1–72)", required: false, default: "24" },
    ],
    sample: `{
  "items": [
    {
      "title": "Booking.com unveils agentic search",
      "source": "Phocuswright",
      "public_clicks": 6,
      "trending_detected_at": "2026-02-14T08:12:00+00:00"
    }
  ],
  "total": 5,
  "window_hours": 24
}`,
  },
  {
    id: "sources",
    method: "GET",
    path: "/api/external/sources",
    icon: Radio,
    name: "Source catalog",
    description: "Editorial sources with article counts, clicks and signal aggregates over the requested window.",
    badge: "NEW",
    params: [
      { name: "days", type: "int (1–90)", required: false, default: "30" },
      { name: "exclude_reddit", type: "bool", required: false, default: "true" },
    ],
    sample: `{
  "sources": [
    {
      "source": "Skift",
      "article_count": 38,
      "total_clicks": 142,
      "total_signal": 91,
      "last_published": "2026-02-14T06:30:00+00:00"
    }
  ],
  "total": 12,
  "window_days": 30
}`,
  },
  {
    id: "weekly-ai-summary",
    method: "GET",
    path: "/api/external/weekly-ai-summary",
    icon: BookOpenCheck,
    name: "Weekly AI summary",
    description: "AI-generated thematic editorial digest of the past N days. Groups news by story arcs (not categories) and writes 80-150 words of prose per theme. 24h cache + daily pre-warm. GPT-4o backend.",
    badge: "NEW",
    params: [
      { name: "window_days", type: "int (1–14)", required: false, default: "7" },
      { name: "max_articles", type: "int (50–500)", required: false, default: "200" },
      { name: "force_refresh", type: "bool", required: false, default: "false" },
      { name: "language", type: "'en' | 'it'", required: false, default: "en" },
    ],
    sample: `{
  "generated_at": "2026-06-05T07:00:00+00:00",
  "window_days": 7,
  "article_count": 187,
  "window_from": "2026-05-29T07:00:00+00:00",
  "window_to": "2026-06-05T07:00:00+00:00",
  "executive_summary": "AI agents continue reshaping how OTAs and direct channels compete...",
  "themes": [
    {
      "id": "ai-agents-vs-ota",
      "title": "AI agents challenge OTA distribution",
      "emoji": "🤖",
      "summary": "Booking.com and Expedia both rolled out agentic search...",
      "article_count": 14,
      "key_articles": [
        { "id": "...", "title": "...", "source": "Skift", "url": "...", "publishedAt": "..." }
      ]
    }
  ],
  "model": "gpt-4o",
  "cache_hit": false,
  "cached_until": "2026-06-06T07:00:00+00:00",
  "language": "en"
}`,
  },
  {
    id: "clusters",
    method: "GET",
    path: "/api/external/clusters",
    icon: Layers,
    name: "Story clusters",
    description: "Raw story clusters — groups of articles describing the same news event across multiple publishers, detected semantically (cosine ≥0.78, 72h window).",
    badge: "NEW",
    params: [
      { name: "limit", type: "int (1–50)", required: false, default: "20" },
      { name: "viral_only", type: "bool", required: false, default: "true" },
      { name: "hours", type: "int (1–168)", required: false, default: "72" },
    ],
    sample: `{
  "clusters": [
    {
      "id": "...",
      "size": 4,
      "sources": ["skift", "phocuswire", "hoteltechreport", "hotel dive"],
      "is_viral": true,
      "first_article_id": "...",
      "last_article_id": "...",
      "article_ids": [...],
      "first_seen_at": "...",
      "last_growth_at": "...",
      "viral_detected_at": "..."
    }
  ],
  "total": 5,
  "window_hours": 72
}`,
  },
  {
    id: "reports",
    method: "GET",
    path: "/api/external/reports",
    icon: FileText,
    name: "Reports & Research",
    description: "Curated industry reports (84+ seed reports from WTTC, OECD, UN Tourism, McKinsey, Skift Research, Amadeus, etc.) plus weekly Firecrawl-discovered additions across 155 source organisations. Sorted by published_date DESC; 18-month soft sort (older still queryable).",
    badge: "NEW",
    params: [
      { name: "limit", type: "int (1–200)", required: false, default: "50" },
      { name: "page", type: "int", required: false, default: "1" },
      { name: "category", type: "string", required: false, default: "—" },
      { name: "priority", type: "string (Alta/Media/Bassa)", required: false, default: "—" },
      { name: "search", type: "string", required: false, default: "—" },
    ],
    sample: `{
  "reports": [
    {
      "id": "...",
      "title": "Artificial Intelligence Adoption in Tourism",
      "organisation": "UN Tourism",
      "category": "Istituzioni globali",
      "url": "https://www.e-unwto.org/...",
      "published_date": "2025-01-01T00:00:00+00:00",
      "report_type": "Report",
      "focus": "AI adoption, stakeholders, governance",
      "access": "Free / DOI",
      "priority": "Alta",
      "discovered_via": "seed"
    }
  ],
  "page": 1,
  "limit": 50,
  "total": 84,
  "has_more": true
}`,
  },
  {
    id: "reports-categories",
    method: "GET",
    path: "/api/external/reports/categories",
    icon: Database,
    name: "Report categories",
    description: "Catalog of report categories with item counts. Use the `category` value as a filter for /external/reports.",
    badge: "NEW",
    params: [],
    sample: `{
  "categories": [
    {"category": "Consulting", "count": 14},
    {"category": "Istituzioni globali", "count": 13},
    {"category": "OTA e marketplace", "count": 11},
    {"category": "Travel tech", "count": 11}
  ],
  "total_reports": 84
}`,
  },
  {
    id: "reports-sources",
    method: "GET",
    path: "/api/external/reports/sources",
    icon: Database,
    name: "Report sources",
    description: "The 155 curated organisations that publish industry research (academia, multilaterals, NTOs, OTAs, big-tech, consultancies, data intelligence providers).",
    badge: "NEW",
    params: [
      { name: "category", type: "string", required: false, default: "—" },
      { name: "limit", type: "int (1–500)", required: false, default: "200" },
    ],
    sample: `{
  "sources": [
    {
      "id": "...",
      "organisation": "UN Tourism",
      "category_macro": "Istituzioni globali e policy",
      "homepage_url": "https://www.unwto.org",
      "report_hub_url": "https://www.e-unwto.org",
      "area": "Global",
      "focus": "Tourism policy, sustainable development",
      "priority": "Alta"
    }
  ],
  "total": 155
}`,
  },
  {
    id: "refresh",
    method: "POST",
    path: "/api/external/refresh",
    icon: Sparkles,
    name: "Trigger refresh",
    description: "Kicks off a background scrape across all sources. Returns immediately; poll `/api/scraping-status` for progress. `started` is true only when a run was really scheduled: `skipped_reason` is `already_running` (single-flight) or `cooldown` (last run completed < 20 min ago → see `retry_after_seconds` / `next_allowed_at`).",
    params: [],
    sample: `{
  "success": true,
  "started": true,
  "skipped_reason": null,
  "already_running": false,
  "cooldown_minutes": 20,
  "last_completed_at": "2026-11-01T06:53:50+00:00",
  "message": "News refresh started in background.",
  "status_endpoint": "/api/scraping-status"
}`,
  },
  {
    id: "health",
    method: "GET",
    path: "/api/health",
    icon: Heart,
    name: "Health & ingestion freshness",
    description: "Public no-auth liveness probe for external monitors (UptimeRobot, StatusCake, Mission Control crons). Carries `last_article_age_h` and `current_run_age_h` so a single keyword check (`\"status\":\"ok\"` must appear) catches DB outage, scraper hung past 1h, OR ingestion stale >24h.",
    badge: "NEW",
    params: [],
    sample: `{
  "status": "ok",
  "db": "connected",
  "checked_at": "2026-06-21T16:25:00.000Z",
  "scraper": {
    "last_completed_at": "2026-06-21T08:34:02.000Z",
    "last_article_age_h": 2.3,
    "current_run_status": "completed",
    "current_run_started_at": "2026-06-21T08:30:00.000Z",
    "current_run_age_h": null,
    "articles_found_last_run": 47
  }
}`,
  },
];

/* ─── Code snippet builders ─────────────────────────────────────────────── */

function buildCurl(ep) {
  const query = ep.params.length ? "?" + ep.params.map(p => `${p.name}=${encodeURIComponent(p.default === "—" ? "" : p.default)}`).filter(s => !s.endsWith("=")).join("&") : "";
  const url = `${BASE}${ep.path}${query}`;
  if (ep.method === "POST") {
    return `curl -X POST "${url}" \\\n  -H "X-API-Key: ti_live_YOUR_KEY_HERE"`;
  }
  return `curl "${url}" \\\n  -H "X-API-Key: ti_live_YOUR_KEY_HERE"`;
}

function buildJs(ep) {
  const url = `${BASE}${ep.path}`;
  if (ep.method === "POST") {
    return `const res = await fetch("${url}", {
  method: "POST",
  headers: { "X-API-Key": "ti_live_YOUR_KEY_HERE" },
});
const data = await res.json();
console.log(data);`;
  }
  return `const res = await fetch("${url}", {
  headers: { "X-API-Key": "ti_live_YOUR_KEY_HERE" },
});
const data = await res.json();
console.log(data);`;
}

function buildPython(ep) {
  const url = `${BASE}${ep.path}`;
  if (ep.method === "POST") {
    return `import requests

res = requests.post(
    "${url}",
    headers={"X-API-Key": "ti_live_YOUR_KEY_HERE"},
)
print(res.json())`;
  }
  return `import requests

res = requests.get(
    "${url}",
    headers={"X-API-Key": "ti_live_YOUR_KEY_HERE"},
)
print(res.json())`;
}

/* ─── Sub-component: CodeBlock with copy ────────────────────────────────── */

function CodeBlock({ code, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={{ position: "relative" }}>
      <button
        data-testid={`copy-${label.toLowerCase()}-btn`}
        onClick={handleCopy}
        style={{
          position: "absolute", top: 10, right: 10, padding: "4px 10px",
          background: copied ? "#006874" : "rgba(255,255,255,0.08)",
          color: copied ? "#fff" : "#9aa6a8",
          border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
          fontSize: 11, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 4,
        }}
      >
        {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre style={{
        margin: 0, background: "#0f1719", color: "#e7e8e9", padding: "16px 18px",
        borderRadius: 12, fontSize: 12.5, lineHeight: 1.6, overflow: "auto",
        fontFamily: "ui-monospace, 'SF Mono', Monaco, Menlo, monospace",
        maxHeight: 420,
      }}>{code}</pre>
    </div>
  );
}

/* ─── Sub-component: Endpoint card ──────────────────────────────────────── */

function EndpointCard({ ep }) {
  const [tab, setTab] = useState("curl");
  const Icon = ep.icon;
  const code = useMemo(() => {
    if (tab === "curl") return buildCurl(ep);
    if (tab === "js") return buildJs(ep);
    return buildPython(ep);
  }, [tab, ep]);

  return (
    <div
      data-testid={`endpoint-card-${ep.id}`}
      id={ep.id}
      style={{
        background: "#fff", border: "1px solid #e8ecee", borderRadius: 16,
        padding: 24, scrollMarginTop: 80,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 14 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: "#f0f9fa",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon style={{ width: 18, height: 18, color: "#006874" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#191c1d", letterSpacing: "-0.01em" }}>{ep.name}</h3>
            {ep.badge && (
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                color: "#fff", background: "#006874", padding: "2px 8px", borderRadius: 99,
              }}>{ep.badge}</span>
            )}
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "#6b7a7d", lineHeight: 1.55 }}>{ep.description}</p>
        </div>
      </div>

      {/* Method + path */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, background: "#f8f9fa",
        padding: "10px 14px", borderRadius: 10, marginBottom: 16,
        fontFamily: "ui-monospace, monospace", fontSize: 13,
      }}>
        <span style={{
          fontWeight: 700, fontSize: 11, padding: "3px 8px", borderRadius: 6,
          background: ep.method === "POST" ? "#fef3c7" : "#dcfce7",
          color: ep.method === "POST" ? "#92400e" : "#166534",
        }}>{ep.method}</span>
        <code style={{ color: "#191c1d", flex: 1, overflow: "auto" }}>{ep.path}</code>
      </div>

      {/* Params */}
      {ep.params.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#6b7a7d" }}>Query parameters</p>
          <div style={{ border: "1px solid #e8ecee", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: "#f8f9fa" }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7a7d", fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7a7d", fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7a7d", fontWeight: 600 }}>Default</th>
                </tr>
              </thead>
              <tbody>
                {ep.params.map(p => (
                  <tr key={p.name} style={{ borderTop: "1px solid #f1f3f4" }}>
                    <td style={{ padding: "8px 12px", color: "#191c1d", fontFamily: "monospace" }}>{p.name}</td>
                    <td style={{ padding: "8px 12px", color: "#3b494c" }}>{p.type}</td>
                    <td style={{ padding: "8px 12px", color: "#6b7a7d", fontFamily: "monospace" }}>{p.default}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, borderBottom: "1px solid #e8ecee" }}>
        {[
          { id: "curl", label: "cURL" },
          { id: "js", label: "JavaScript" },
          { id: "python", label: "Python" },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}-${ep.id}`}
            onClick={() => setTab(t.id)}
            style={{
              padding: "8px 14px", border: "none", background: "transparent",
              fontSize: 12.5, fontWeight: 600, cursor: "pointer",
              color: tab === t.id ? "#006874" : "#6b7a7d",
              borderBottom: tab === t.id ? "2px solid #006874" : "2px solid transparent",
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      <CodeBlock code={code} label={tab} />

      {/* Sample response */}
      <p style={{ margin: "18px 0 8px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#6b7a7d" }}>Sample response</p>
      <CodeBlock code={ep.sample} label="response" />
    </div>
  );
}

/* ─── Sub-component: Request access form ────────────────────────────────── */

function RequestAccessForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", website: "", use_case: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.use_case.trim().length < 20) {
      toast.error("Please describe your use case in at least 20 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/external/request-key`, form);
      setDone(true);
      toast.success("Request received — we'll reply within 2 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="request-success"
        style={{
          background: "#f0f9fa", border: "1px solid #006874", borderRadius: 14,
          padding: 28, textAlign: "center",
        }}
      >
        <Check style={{ width: 36, height: 36, color: "#006874", margin: "0 auto 8px" }} />
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#191c1d" }}>Request received</h3>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: "#3b494c", lineHeight: 1.6 }}>
          Thanks — we review every application personally. You&apos;ll hear back at <strong>{form.email}</strong> within 2 business days.
        </p>
      </div>
    );
  }

  const inputStyle = {
    width: "100%", padding: "10px 12px", border: "1px solid #e8ecee",
    borderRadius: 10, fontSize: 14, color: "#191c1d", fontFamily: "inherit",
    outline: "none", background: "#fff",
  };
  const labelStyle = { display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600, color: "#3b494c" };

  return (
    <form onSubmit={submit} data-testid="request-key-form" style={{
      background: "#fff", border: "1px solid #e8ecee", borderRadius: 16, padding: 28,
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <label style={labelStyle}>Full name *</label>
          <input data-testid="form-name-input" required minLength={2} value={form.name} onChange={update("name")} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Work email *</label>
          <input data-testid="form-email-input" required type="email" value={form.email} onChange={update("email")} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Company</label>
          <input data-testid="form-company-input" value={form.company} onChange={update("company")} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Role</label>
          <input data-testid="form-role-input" value={form.role} onChange={update("role")} placeholder="e.g. CTO, Head of Data" style={inputStyle} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Website (optional)</label>
          <input data-testid="form-website-input" type="url" value={form.website} onChange={update("website")} placeholder="https://" style={inputStyle} />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Use case * <span style={{ color: "#6b7a7d", fontWeight: 400 }}>(minimum 20 chars)</span></label>
          <textarea
            data-testid="form-usecase-input"
            required minLength={20} rows={5}
            value={form.use_case} onChange={update("use_case")}
            placeholder="Describe how you'll use the API — what you're building, expected volume, target audience."
            style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>
      </div>
      <div style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <p style={{ margin: 0, fontSize: 12, color: "#6b7a7d", display: "flex", alignItems: "center", gap: 6 }}>
          <Lock style={{ width: 12, height: 12 }} />
          Every request is reviewed personally — no automated approval.
        </p>
        <button
          type="submit"
          data-testid="submit-request-btn"
          disabled={submitting}
          style={{
            padding: "11px 22px", background: "#006874", color: "#fff",
            border: "none", borderRadius: 99, fontSize: 13, fontWeight: 700,
            cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.6 : 1,
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          <Send style={{ width: 14, height: 14 }} />
          {submitting ? "Sending..." : "Request access"}
        </button>
      </div>
    </form>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */

export default function ApiDocsPage() {
  useEffect(() => {
    document.title = "API Docs (BETA) — TourismIntel";
  }, []);

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Top bar — shared across all public/content pages */}
      <PublicPageNav active="api" />

      {/* Hero */}
      <section style={{ padding: "56px 24px 32px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#006874" }}>Developer</p>
          <h1 data-testid="api-docs-title" style={{ margin: "10px 0 12px", fontSize: "clamp(32px, 5vw, 48px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#191c1d", fontFamily: "'Reckless Neue Variable', Georgia, serif" }}>
            The TourismIntel API
          </h1>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.55, color: "#3b494c", maxWidth: 680 }}>
            Eleven production endpoints to pipe daily travel intelligence into your own
            dashboards, Slack channels, internal portals, agents and analytics
            stacks. Authentication is a single <code style={{ background: "#f0f9fa", padding: "1px 6px", borderRadius: 6, color: "#006874", fontSize: 14 }}>X-API-Key</code> header.
          </p>
          <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href="#request" data-testid="hero-request-link" style={{
              padding: "10px 18px", background: "#006874", color: "#fff",
              borderRadius: 99, fontSize: 13, fontWeight: 700, textDecoration: "none",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <Key style={{ width: 14, height: 14 }} /> Request an API key
            </a>
            <a href="#endpoints" style={{
              padding: "10px 18px", background: "#fff", color: "#006874",
              border: "1px solid #006874", borderRadius: 99, fontSize: 13, fontWeight: 700,
              textDecoration: "none", display: "flex", alignItems: "center", gap: 6,
            }}>
              <Code2 style={{ width: 14, height: 14 }} /> Browse endpoints
            </a>
          </div>
        </div>
      </section>

      {/* Quickstart */}
      <section style={{ padding: "24px 24px 8px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{
            background: "linear-gradient(135deg, #0f1719 0%, #1a2628 100%)",
            borderRadius: 16, padding: 28, color: "#e7e8e9",
          }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9aa6a8" }}>Quickstart</p>
            <h2 style={{ margin: "8px 0 14px", fontSize: 22, fontWeight: 700, color: "#fff" }}>Make your first call in 30 seconds</h2>
            <pre style={{
              margin: 0, background: "rgba(255,255,255,0.04)", padding: "14px 16px",
              borderRadius: 10, fontSize: 13, color: "#e7e8e9",
              fontFamily: "ui-monospace, monospace", overflow: "auto",
              border: "1px solid rgba(255,255,255,0.08)",
            }}>{`curl "${BASE}/api/external/brief" \\
  -H "X-API-Key: ti_live_YOUR_KEY_HERE"`}</pre>
            <p style={{ margin: "14px 0 0", fontSize: 13, color: "#9aa6a8", lineHeight: 1.6 }}>
              Rate limits are generous during BETA — but please be mindful. Production
              limits will be communicated when we exit beta. Reach out if you need a
              higher allowance.
            </p>
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section id="endpoints" style={{ padding: "40px 24px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700, color: "#191c1d", letterSpacing: "-0.02em", fontFamily: "'Reckless Neue Variable', Georgia, serif" }}>Endpoints</h2>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: "#6b7a7d" }}>
            All endpoints return JSON. Authenticate with <code style={{ background: "#f3f4f5", padding: "1px 6px", borderRadius: 6, fontSize: 12.5 }}>X-API-Key: ti_live_…</code> on every request.
          </p>

          {/* TOC */}
          <div style={{
            background: "#fff", border: "1px solid #e8ecee", borderRadius: 14, padding: 16,
            marginBottom: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 6,
          }}>
            {ENDPOINTS.map(ep => {
              const Icon = ep.icon;
              return (
                <a
                  key={ep.id}
                  href={`#${ep.id}`}
                  data-testid={`toc-${ep.id}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                    borderRadius: 8, textDecoration: "none", color: "#3b494c", fontSize: 13,
                    fontWeight: 500, transition: "background 0.15s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "#f0f9fa")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <Icon style={{ width: 14, height: 14, color: "#006874", flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{ep.name}</span>
                  {ep.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                      color: "#006874", background: "#e0f4f6", padding: "1px 6px", borderRadius: 99,
                    }}>{ep.badge}</span>
                  )}
                </a>
              );
            })}
          </div>

          <div style={{ display: "grid", gap: 20 }}>
            {ENDPOINTS.map(ep => <EndpointCard key={ep.id} ep={ep} />)}
          </div>
        </div>
      </section>

      {/* Request access */}
      <section id="request" style={{ padding: "32px 24px 64px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <Key style={{ width: 28, height: 28, color: "#006874", marginBottom: 8 }} />
            <h2 style={{ margin: "0 0 8px", fontSize: 26, fontWeight: 700, color: "#191c1d", letterSpacing: "-0.02em", fontFamily: "'Reckless Neue Variable', Georgia, serif" }}>
              Request an API key
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: "#6b7a7d", lineHeight: 1.6 }}>
              This is an experiment! I don&apos;t issue keys automatically. Tell me what you&apos;re building and maybe I&apos;ll get back to you soon :)
            </p>
          </div>
          <RequestAccessForm />
        </div>
      </section>

      {/* Footer */}
      <PublicPageFooter />
    </div>
  );
}
